import axiosInstance from '../utils/axiosConfig'
import { mobileBackendUrl } from '../env'
import {
  MFExecutionData,
  InvestPaymentMethodType,
  OrderOTPTriggerPayload,
  OrderPayload,
  OrderResponse,
  PaymentStatusResponse,
  SystematicOTPTriggerPayload,
  SystematicResponse,
  SystematicPayload,
  AutoPayMethodResponse,
  MandateDetailsResponse,
  SetupAutoPayPayload,
  SetupAutoPayResponse,
  AutoPayStatusPayload,
  AutoPayStatusResponse,
  OrderOTPTriggerResponse,
  SystematicOTPTriggerResponse,
  TaxCalculationsPayload,
  TaxCalculationsResponse,
  FetchUserBankAccountsResponse,
  UPISetupResponse,
  UPISetupPayload,
  PaymentDetailsResponse
} from '../types/mf-execution.types'

export const fetchMFExecutionData = async (isin: string): Promise<MFExecutionData> => {
  const response = await axiosInstance.get<MFExecutionData>(
    `${mobileBackendUrl}/v2/mutual-fund/execution/validations/${isin}`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch MF execution data')
  }
  return response?.data as MFExecutionData
}

export const fetchUserInvestPaymentMethods = async (): Promise<InvestPaymentMethodType[]> => {
  const response = await axiosInstance.get<InvestPaymentMethodType[]>(
    `${mobileBackendUrl}/v2/mutual-fund/execution/payment-methods`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch user payment methods')
  }
  return response?.data as InvestPaymentMethodType[]
}

export const fetchUserInvestPaymentMethodsV2 = async (): Promise<PaymentDetailsResponse> => {
  const response = await axiosInstance.get<PaymentDetailsResponse>(
    `${mobileBackendUrl}/v2/mutual-fund/execution/payment-details`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch user payment methods')
  }
  return response?.data
}

export const triggerOrderOTP = async (payload: OrderOTPTriggerPayload): Promise<OrderOTPTriggerResponse> => {
  const response = await axiosInstance.post(`${mobileBackendUrl}/v2/mutual-fund/execution/order/otp`, payload)
  if (!response?.data) {
    throw new Error('Failed to trigger Order OTP')
  }
  return response?.data as any
}

export const triggerSystematicOTP = async (
  payload: SystematicOTPTriggerPayload
): Promise<SystematicOTPTriggerResponse> => {
  const response = await axiosInstance.post(`${mobileBackendUrl}/v2/mutual-fund/execution/order/otp`, payload)
  if (!response?.data) {
    throw new Error('Failed to trigger Systematic OTP')
  }
  return response?.data as any
}

export const createOrder = async (payload: OrderPayload): Promise<OrderResponse> => {
  const response = await axiosInstance.post(`${mobileBackendUrl}/v2/mutual-fund/execution/order`, payload)
  if (!response?.data) {
    throw new Error('Failed to create order')
  }
  return response?.data as OrderResponse
}

export const createSystematic = async (payload: SystematicPayload): Promise<SystematicResponse> => {
  const response = await axiosInstance.post(`${mobileBackendUrl}/v2/mutual-fund/execution/systematic`, payload)
  if (!response?.data) {
    throw new Error('Failed to create systematic order')
  }
  return response?.data as SystematicResponse
}

export const fetchOrderPaymentStatus = async (orderId: string): Promise<PaymentStatusResponse> => {
  const response = await axiosInstance.get(
    `${mobileBackendUrl}/v2/mutual-fund/execution/order/${orderId}/payment-status`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch payment status')
  }
  return response?.data as PaymentStatusResponse
}

export const fetchSystematicPaymentStatus = async (sxpId: string): Promise<PaymentStatusResponse> => {
  const response = await axiosInstance.get(
    `${mobileBackendUrl}/v2/mutual-fund/execution/systematic/${sxpId}/payment-status`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch payment status')
  }
  return response?.data as PaymentStatusResponse
}

export const fetchAutoPayMethods = async (sxpId: string): Promise<AutoPayMethodResponse> => {
  const response = await axiosInstance.get(`${mobileBackendUrl}/v2/mutual-fund/execution/systematic/${sxpId}/mandates`)
  if (!response?.data) {
    throw new Error('Failed to fetch auto pay methods')
  }
  return response?.data as AutoPayMethodResponse
}

/** V2 mandate-details for Setup Autopay: SIP date, limits, mandate type, and per-account mandate state. */
export const fetchMandateDetails = async (sxpId: string): Promise<MandateDetailsResponse> => {
  const response = await axiosInstance.get<MandateDetailsResponse>(
    `${mobileBackendUrl}/v2/mutual-fund/execution/systematic/${sxpId}/mandate-details`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch mandate details')
  }
  return response?.data
}

export const setupAutoPay = async (payload: SetupAutoPayPayload): Promise<SetupAutoPayResponse> => {
  const response = await axiosInstance.post(`${mobileBackendUrl}/v2/mutual-fund/execution/mandate`, payload)
  if (!response?.data) {
    throw new Error('Failed to setup auto pay')
  }
  return response?.data as SetupAutoPayResponse
}

export const fetchAutoPayStatus = async (payload: AutoPayStatusPayload): Promise<AutoPayStatusResponse> => {
  const params = new URLSearchParams({
    sxpId: payload.sxpId,
    mandateId: payload.mandateId,
  })

  // Required when v2 setup used a UPI app (SetupAutoPay → status/link-waiting URL).
  if (payload.upiPackageName) {
    params.set('upiPackageName', payload.upiPackageName)
  }

  const response = await axiosInstance.get(
    `/v2/mutual-fund/execution/sxp-mandate/status?${params.toString()}`
  )
  return response.data
}

const maskDisplayAccNumber = (displayAccNumber: string): string => {
  if (!displayAccNumber) return displayAccNumber

  // Check if already in masked format (contains "****")
  if (displayAccNumber?.includes('****')) {
    return displayAccNumber
  }

  // Extract bank name (first word or words before any numbers)
  const bankNameMatch = displayAccNumber?.match(/^([A-Za-z\s]+)/)
  const bankName = bankNameMatch ? bankNameMatch?.[1]?.trim() : ''

  // Extract all digits from the account number
  const digits = displayAccNumber?.replace(/\D/g, '')
  const last4Digits = digits?.length >= 4 ? digits?.slice(-4) : digits

  // If we have both bank name and digits, format as "BankName **** 1234"
  if (bankName && last4Digits) {
    return `${bankName} **** ${last4Digits}`
  }

  // If only digits found, mask them
  if (digits && digits?.length >= 4) {
    return `**** ${last4Digits}`
  }

  // Fallback: return as is if format is unexpected
  return displayAccNumber
}

export const fetchUserBankAccounts = async (folioNumber: string): Promise<FetchUserBankAccountsResponse> => {
  const response = await axiosInstance.get<FetchUserBankAccountsResponse>(
    `${mobileBackendUrl}/v2/mutual-fund/execution/bank-accounts?folioNumber=${folioNumber}`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch user bank accounts')
  }

  // Map response and mask displayAccNumber
  return {
    ...response.data,
    folioAccountDetailsList: response.data.folioAccountDetailsList.map((account) => ({
      ...account,
      displayAccNumber: maskDisplayAccNumber(account.displayAccNumber)
    }))
  }
}

export const fetchTaxCalculations = async (payload: TaxCalculationsPayload): Promise<TaxCalculationsResponse> => {
  let url = `${mobileBackendUrl}/v2/mutual-fund/execution/compute-tax-liability?folioNumber=${payload?.folioNumber}&isin=${payload?.isin}`
  if (payload?.amount) {
    url += `&amount=${payload.amount}`
  }
  const response = await axiosInstance.get(url)
  if (!response?.data) {
    throw new Error('Failed to fetch tax calculations')
  }
  return response?.data as TaxCalculationsResponse
}

export const setupUPI = async (payload: UPISetupPayload): Promise<UPISetupResponse> => {
  const response = await axiosInstance.post(`${mobileBackendUrl}/v2/mutual-fund/execution/setup-upi`, payload)
  if (!response?.data) {
    throw new Error('Failed to setup UPI')
  }
  return response?.data as UPISetupResponse
}
